/**
 * Composer key mapping and intent applicability (prompt.md §20, §48, §78).
 *
 * The decision is recomputed from the live run state at the moment of the action,
 * so a run that changed under the user's fingers refuses the requested intent
 * instead of silently turning a queue into a send or a steer into a new message.
 */

export type ComposerIntent = "send" | "queue" | "steer";

export const COMPOSER_KEY_BINDINGS = {
  send: "Enter",
  newline: "Ctrl+Enter",
  queue: "Alt+Enter",
  steer: "Ctrl+Alt+Enter",
} as const;

export interface ComposerKeyEvent {
  readonly key: string;
  readonly shiftKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly isComposing: boolean;
}

export interface ComposerRunState {
  readonly runActive: boolean;
  readonly steerable: boolean;
  readonly queueAvailable?: boolean;
}

export interface ComposerKeyDecision {
  readonly handled: boolean;
  readonly intent: ComposerIntent | null;
  readonly newline: boolean;
  readonly refusal: string | null;
}

export function intentAvailability(state: ComposerRunState): { readonly send: true; readonly queue: boolean; readonly steer: boolean } {
  return { send: true, queue: state.runActive && state.queueAvailable !== false, steer: state.runActive && state.steerable };
}

export function intentRefusal(intent: ComposerIntent, state: ComposerRunState): string | null {
  if (intent === "send") return null;
  if (!state.runActive) {
    return intent === "queue"
      ? "Queueing needs an active run; send the message instead."
      : "Steering needs an active run; send the message instead.";
  }
  if (intent === "queue" && state.queueAvailable === false) return "This client cannot queue while the run is active.";
  if (intent === "steer" && !state.steerable) return "The selected run cannot be steered; queue or send instead.";
  return null;
}

export function composerKeyDecision(event: ComposerKeyEvent, state: ComposerRunState): ComposerKeyDecision {
  if (event.isComposing) return { handled: false, intent: null, newline: false, refusal: null };
  if (event.key !== "Enter") return { handled: false, intent: null, newline: false, refusal: null };
  if (event.shiftKey) return { handled: false, intent: null, newline: true, refusal: null };
  const command = event.ctrlKey || event.metaKey;
  if (command && !event.altKey) return { handled: true, intent: null, newline: true, refusal: null };
  const intent: ComposerIntent = command && event.altKey ? "steer" : event.altKey ? "queue" : "send";
  const refusal = intentRefusal(intent, state);
  if (refusal !== null) return { handled: true, intent: null, newline: false, refusal };
  return { handled: true, intent, newline: false, refusal: null };
}

/** Insert a blank line below the caret's line, preserving the rest of that line. */
export function composerLineBelow(text: string, caret: number): { readonly text: string; readonly caret: number } {
  const end = text.indexOf("\n", Math.max(0, Math.min(caret, text.length)));
  const position = end === -1 ? text.length : end;
  return { text: `${text.slice(0, position)}\n${text.slice(position)}`, caret: position + 1 };
}

export function insertComposerLineBelow(element: HTMLTextAreaElement, setText: (text: string) => void): void {
  const next = composerLineBelow(element.value, element.selectionStart);
  setText(next.text);
  requestAnimationFrame(() => {
    if (element.isConnected) element.setSelectionRange(next.caret, next.caret);
  });
}

export function intentControlLabel(intent: ComposerIntent, availability: ReturnType<typeof intentAvailability>): string {
  if (intent === "queue") return availability.queue ? `Queue (${COMPOSER_KEY_BINDINGS.queue})` : "Queue unavailable";
  if (intent === "steer") return availability.steer ? `Steer (${COMPOSER_KEY_BINDINGS.steer})` : "Steer unavailable";
  return `Send (${COMPOSER_KEY_BINDINGS.send})`;
}

/** A queued action is either local to this device or durable in the workspace. */
export function queueLocationLabel(local: boolean): string {
  return local ? "Queued on this device; keep the app open." : "Queued durably in this workspace.";
}
